import { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useStudentProfile } from '../../hooks/useStudentProfile';
import StudentLayout from '../../components/student/StudentLayout';

const StudentMigrationCertificatePage = () => {
  const navigate = useNavigate();
  const { data: profile, isLoading } = useStudentProfile();
  const migrationRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Generate 6-digit serial number from registrationNo
  const getSerialNo = (registrationNo: string): string => {
    if (!registrationNo) return '000000';
    
    // Remove any non-digit characters
    const digits = registrationNo.replace(/\D/g, '');
    
    if (digits.length === 0) return '000000';
    
    // Take last 6 digits, or pad with zeros if shorter
    if (digits.length >= 6) {
      return digits.slice(-6);
    } else {
      // Pad with zeros to make it 6 digits
      return digits.padStart(6, '0');
    }
  };

  const serialNo = profile ? getSerialNo(profile.registrationNo) : '000000';

  const handleMenuItemClick = (item: string) => {
    switch (item) {
      case 'profile':
        navigate('/student-dashboard');
        break;
      case 'admitCard':
        navigate('/student/admit-card');
        break;
      case 'marksheet':
        navigate('/student/marksheet');
        break;
      case 'certificate':
        navigate('/student/certificate');
        break;
      case 'migration':
        navigate('/student/migration');
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <StudentLayout activeMenuItem="migration" pageTitle="Migration Certificate" onMenuItemClick={handleMenuItemClick}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </StudentLayout>
    );
  }

  if (!profile?.isMigrationApproved) {
    return (
      <StudentLayout activeMenuItem="migration" pageTitle="Migration Certificate" onMenuItemClick={handleMenuItemClick}>
        <Box sx={{ p: 3 }}>
          <Alert severity="info">Your migration certificate is not yet approved. Please contact your center.</Alert>
        </Box>
      </StudentLayout>
    );
  }

  const handleDownload = async () => {
    if (migrationRef.current && profile) {
      setIsDownloading(true);
      try {
        // Wait for all images to load before capturing
        const images = migrationRef.current.querySelectorAll('img');
        await Promise.all(
          Array.from(images).map((img) => {
            return new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) {
                resolve(true);
              } else {
                img.onload = () => resolve(true);
                img.onerror = () => resolve(true); // Continue even if image fails to load
              }
            });
          })
        );

        // Additional wait to ensure everything is rendered
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get the SVG image to determine actual content dimensions
        const svgImage = migrationRef.current.querySelector('img[src*="migration-certificate"]') as HTMLImageElement;
        let width = migrationRef.current.clientWidth;
        let height = migrationRef.current.clientHeight;
        
        // If SVG is loaded, use its natural dimensions (this avoids white space)
        if (svgImage && svgImage.complete && svgImage.naturalWidth > 0 && svgImage.naturalHeight > 0) {
          width = svgImage.naturalWidth;
          height = svgImage.naturalHeight;
        } else {
          // Fallback: use client dimensions
          const rect = migrationRef.current.getBoundingClientRect();
          width = rect.width;
          height = rect.height;
        }
        
        // Use higher scale for better quality (4x for 300 DPI equivalent)
        // 300 DPI = 300 pixels per inch, so for a ~11 inch width, we need ~3300px
        // Scale of 4 gives us 4x the resolution which is good for print quality
        const scale = 4;
        const dpi = 300;
        
        const canvas = await html2canvas(migrationRef.current, {
          backgroundColor: '#ffffff',
          scale: scale,
          useCORS: true,
          allowTaint: true,
          logging: false,
          width: width,
          height: height,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: (clonedDoc) => {
            // Ensure all fonts are loaded
            const clonedWindow = clonedDoc.defaultView;
            if (clonedWindow) {
              clonedWindow.document.fonts.ready;
            }
            // Ensure SVG images are rendered at high resolution
            const clonedImages = clonedDoc.querySelectorAll('img');
            clonedImages.forEach((img) => {
              if (img.src.endsWith('.svg')) {
                // Force high resolution rendering for SVG
                (img as HTMLImageElement).style.imageRendering = 'auto';
              }
            });
          },
        });
        
        // Crop canvas to remove white space on all sides
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const pixels = imageData.data;
          
          // Find content bounds (top, left, bottom, right)
          let top = 0, left = 0, bottom = canvas.height, right = canvas.width;
          
          // Find top (first row with non-white content)
          for (let y = 0; y < canvas.height; y++) {
            let hasContent = false;
            for (let x = 0; x < canvas.width; x++) {
              const idx = (y * canvas.width + x) * 4;
              const r = pixels[idx];
              const g = pixels[idx + 1];
              const b = pixels[idx + 2];
              if (r < 250 || g < 250 || b < 250) {
                hasContent = true;
                break;
              }
            }
            if (hasContent) {
              top = Math.max(0, y - 5); // Small padding
              break;
            }
          }
          
          // Find bottom (last row with non-white content)
          for (let y = canvas.height - 1; y >= 0; y--) {
            let hasContent = false;
            for (let x = 0; x < canvas.width; x++) {
              const idx = (y * canvas.width + x) * 4;
              const r = pixels[idx];
              const g = pixels[idx + 1];
              const b = pixels[idx + 2];
              if (r < 250 || g < 250 || b < 250) {
                hasContent = true;
                break;
              }
            }
            if (hasContent) {
              bottom = Math.min(canvas.height, y + 5); // Small padding
              break;
            }
          }
          
          // Find left (first column with non-white content)
          for (let x = 0; x < canvas.width; x++) {
            let hasContent = false;
            for (let y = top; y < bottom; y++) {
              const idx = (y * canvas.width + x) * 4;
              const r = pixels[idx];
              const g = pixels[idx + 1];
              const b = pixels[idx + 2];
              if (r < 250 || g < 250 || b < 250) {
                hasContent = true;
                break;
              }
            }
            if (hasContent) {
              left = Math.max(0, x - 5); // Small padding
              break;
            }
          }
          
          // Find right (last column with non-white content)
          for (let x = canvas.width - 1; x >= 0; x--) {
            let hasContent = false;
            for (let y = top; y < bottom; y++) {
              const idx = (y * canvas.width + x) * 4;
              const r = pixels[idx];
              const g = pixels[idx + 1];
              const b = pixels[idx + 2];
              if (r < 250 || g < 250 || b < 250) {
                hasContent = true;
                break;
              }
            }
            if (hasContent) {
              right = Math.min(canvas.width, x + 5); // Small padding
              break;
            }
          }
          
          // Crop the canvas
          if (left > 0 || top > 0 || right < canvas.width || bottom < canvas.height) {
            const croppedCanvas = document.createElement('canvas');
            const croppedWidth = right - left;
            const croppedHeight = bottom - top;
            croppedCanvas.width = croppedWidth;
            croppedCanvas.height = croppedHeight;
            const croppedCtx = croppedCanvas.getContext('2d');
            if (croppedCtx) {
              croppedCtx.drawImage(canvas, left, top, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight);
              // Update dimensions for PDF
              width = croppedWidth / scale;
              height = croppedHeight / scale;
              // Use cropped canvas
              // const tempCanvas = canvas;
              canvas.width = croppedWidth;
              canvas.height = croppedHeight;
              ctx.drawImage(croppedCanvas, 0, 0);
            }
          }
        }
        
        // Use high-quality JPEG instead of PNG for much smaller file size
        // Quality 0.98 provides near-lossless quality
        const imgData = canvas.toDataURL('image/jpeg', 0.98);
        
        // Convert original element dimensions to mm for PDF at target DPI
        // The canvas is scaled, but we want PDF dimensions based on original size at 300 DPI
        // Formula: pixels / DPI * 25.4mm per inch
        const pdfWidth = (width / dpi) * 25.4;
        const pdfHeight = (height / dpi) * 25.4;
        
        // Create PDF with proper dimensions in mm
        const pdf = new jsPDF({
          orientation: width > height ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [pdfWidth, pdfHeight],
          compress: true,
        });
        
        // Add high-resolution image - JPEG compression is already applied in toDataURL
        // Use 'FAST' compression for PDF structure (doesn't affect image quality)
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        
        const fileName = `migration-certificate-${profile.registrationNo}-${profile.candidateName.replace(/\s+/g, '-')}.pdf`;
        pdf.save(fileName);
      } catch (error) {
        console.error('Error generating migration certificate PDF:', error);
        alert('Error generating migration certificate PDF. Please try again.');
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <StudentLayout activeMenuItem="migration" pageTitle="Migration Certificate" onMenuItemClick={handleMenuItemClick}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownload}
            disabled={isDownloading}
            sx={{
              backgroundColor: '#10b981',
              '&:hover': { backgroundColor: '#059669' },
            }}
          >
            {isDownloading ? 'Generating PDF...' : 'Download Migration Certificate (PDF)'}
          </Button>
        </Box>

        <Paper 
          ref={migrationRef}
          sx={{ 
            p: 0, 
            m: 0,
            textAlign: 'center', 
            width: { xs: '100%', md: '1056px' },
            display: 'inline-block',
            position: 'relative',
            overflow: 'visible',
            margin: '0 auto',
            boxShadow: 'none',
            backgroundColor: 'transparent',
          }}
        >
          {/* SVG Background */}
          <Box
            component="img"
            src="/mivps-migration-certificate.svg"
            alt="Migration Certificate Template"
            sx={{
              width: '100%',
              height: 'auto',
              display: 'block',
              zIndex: 0,
            }}
          />
          
          {/* Student Details Overlay - Adjust positions based on actual SVG template */}
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1 
          }}>
            {/* Serial No in top left corner */}
            <Box sx={{ position: 'absolute', left: '170px', top: '88px' }}>
              <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.1rem' }}>
                {serialNo}
              </Typography>
            </Box>
            
            <Box sx={{ position: 'absolute', left: '207px', top: '335px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.3rem', fontFamily: '"Dancing Script", cursive' }}>
                {profile.candidateName}
              </Typography>
            </Box>
            
            <Box sx={{ position: 'absolute', left: '620px', top: '335px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.3rem', fontFamily: '"Dancing Script", cursive' }}>
                {profile.fatherName}
              </Typography>
            </Box>
            
            <Box sx={{ position: 'absolute', left: '267px', top: '371px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.5rem', fontFamily: '"Dancing Script", cursive' }}>
                {typeof (profile as any).course === 'object' && (profile as any).course?.name 
                  ? (profile as any).course.name 
                  : profile.course}
              </Typography>
            </Box>
            
            <Box sx={{ position: 'absolute', left: '770px', top: '419px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.3rem', fontFamily: '"Dancing Script", cursive' }}>
                {profile.session}
              </Typography>
            </Box>
            
            {/* Center Name */}
            <Box sx={{ position: 'absolute', left: '123px', top: '422px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.1rem', fontFamily: '"Dancing Script", cursive' }}>
                {typeof (profile as any).centerId === 'object' && (profile as any).centerId?.centerDetails?.centerName
                  ? (profile as any).centerId.centerDetails.centerName
                  : (profile as any).centerName || 'N/A'}
              </Typography>
            </Box>
            
            {/* Enrollment No (Registration No) */}
            <Box sx={{ position: 'absolute', left: '500px', top: '463px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.3rem', fontFamily: '"Dancing Script", cursive' }}>
                {profile.registrationNo || 'N/A'}
              </Typography>
            </Box>
            
            {/* Place - Hardcoded as New Delhi */}
            <Box sx={{ position: 'absolute', left: '435px', top: '636px' }}>
              <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.2rem' }}>
                New Delhi
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </StudentLayout>
  );
};

export default StudentMigrationCertificatePage;
